/**
 * Trade / Business Vertical Terminology Helper
 * Dynamically provides contextual navigation labels, page titles, and button texts
 * based on the active company's selected Trade / Business Vertical:
 * - PHARMACY: Pharmacy / Medical Store
 * - RESTAURANT: Restaurant / Cafe / Food & Beverage
 * - GROCERY: Grocery / Supermarket / Retail Mart
 * - WHOLESALE: Wholesale / Distribution / B2B Trading
 * - ALL_IN_ONE: All-in-One / General Hybrid
 */

import { useAuthStore } from "../stores/authStore";

export type BusinessVertical =
  | "ALL_IN_ONE"
  | "RESTAURANT"
  | "GROCERY"
  | "PHARMACY"
  | "WHOLESALE"
  | string;

export interface TradeTerminology {
  vertical: BusinessVertical;
  verticalLabel: string;

  // POS / Sales
  posButtonLabel: string;
  posNavText: string;
  posPath: string;
  salesReportLabel: string;

  // Catalog & Items
  itemSingular: string;
  itemPlural: string;
  productMasterLabel: string;
  productMasterAddButton: string;
  productListLabel: string;
  productListAddButton: string;
  importAllButtonLabel: string;

  // Categories
  categorySingular: string;
  categoryPlural: string;
  categoryNavText: string;
  categoryAddButton: string;

  // Tables & Recipes
  tablesLabel: string;
  tablesAddButton: string;
  recipesLabel: string;
  recipesAddButton: string;

  // Stakeholders
  customersLabel: string;
  customerSingular: string;
  customerAddButton: string;
  suppliersLabel: string;
  supplierSingular: string;
  supplierAddButton: string;

  // Stock / Operations
  stockOutletLabel: string;
  purchasesLabel: string;
  purchaseAddButton: string;
  workCategorySimpleLabel: string;

  // Sales Report & Returns
  salesInvoicesTabLabel: string;
  returnsTabLabel: string;
  returnsRegisterLabel: string;
  returnSlipTitle: string;
}

export function getTradeTerminology(businessType?: string | null): TradeTerminology {
  const norm = String(businessType || "ALL_IN_ONE").toUpperCase();

  switch (norm) {
    case "PHARMACY":
      return {
        vertical: "PHARMACY",
        verticalLabel: "Pharmacy / Medical",
        posButtonLabel: "Pharmacy POS",
        posNavText: "Pharmacy Billing (POS)",
        posPath: "/sales",
        salesReportLabel: "Pharmacy Sales Report",
        itemSingular: "Medicine",
        itemPlural: "Medicines",
        productMasterLabel: "Medicine Master",
        productMasterAddButton: "Add Medicine",
        productListLabel: "Medicine List",
        productListAddButton: "Add Medicine",
        importAllButtonLabel: "Import All Medicines",
        categorySingular: "Medicine Category",
        categoryPlural: "Medicine Categories",
        categoryNavText: "Medicine Categories",
        categoryAddButton: "Add Medicine Category",
        tablesLabel: "Counter Desks",
        tablesAddButton: "Add Desk",
        recipesLabel: "Formulations / Compounding",
        recipesAddButton: "Add Formulation",
        customersLabel: "Patients / Customers",
        customerSingular: "Patient",
        customerAddButton: "Add Patient",
        suppliersLabel: "Pharma Distributors",
        supplierSingular: "Distributor",
        supplierAddButton: "Add Distributor",
        stockOutletLabel: "Medicine Stock & Expiry",
        purchasesLabel: "Stock Inward (Purchases)",
        purchaseAddButton: "New Inward Entry",
        workCategorySimpleLabel: "Pharmacy / Counter Billing",
        salesInvoicesTabLabel: "Sales Invoices",
        returnsTabLabel: "Returns & Credit Notes",
        returnsRegisterLabel: "Medicine Returns & Credit Notes Register",
        returnSlipTitle: "Credit Note / Return Slip",
      };

    case "RESTAURANT":
      return {
        vertical: "RESTAURANT",
        verticalLabel: "Restaurant / Cafe",
        posButtonLabel: "Restaurant POS",
        posNavText: "Restaurant POS (KOT)",
        posPath: "/reports/sales",
        salesReportLabel: "Restaurant & KOT Report",
        itemSingular: "Dish / Menu Item",
        itemPlural: "Menu Items",
        productMasterLabel: "Menu Master",
        productMasterAddButton: "Add Menu Item",
        productListLabel: "Menu Items",
        productListAddButton: "Add Menu Item",
        importAllButtonLabel: "Import All Menu Items",
        categorySingular: "Menu Category",
        categoryPlural: "Menu Categories",
        categoryNavText: "Menu Categories",
        categoryAddButton: "Add Menu Category",
        tablesLabel: "Dining Tables",
        tablesAddButton: "Add Table",
        recipesLabel: "Recipes & Food Cost",
        recipesAddButton: "Add Recipe",
        customersLabel: "Guests / Customers",
        customerSingular: "Guest",
        customerAddButton: "Add Guest",
        suppliersLabel: "F&B Suppliers",
        supplierSingular: "Supplier",
        supplierAddButton: "Add Supplier",
        stockOutletLabel: "Kitchen & Bar Stock",
        purchasesLabel: "Raw Material Purchases",
        purchaseAddButton: "New Purchase",
        workCategorySimpleLabel: "Quick Counter / Takeaway",
        salesInvoicesTabLabel: "Sales Invoices",
        returnsTabLabel: "Returns & Credit Notes",
        returnsRegisterLabel: "Returns & Credit Notes Register",
        returnSlipTitle: "Credit Note / Return Slip",
      };

    case "GROCERY":
      return {
        vertical: "GROCERY",
        verticalLabel: "Grocery / Supermarket",
        posButtonLabel: "Retail POS",
        posNavText: "Retail Barcode POS",
        posPath: "/sales",
        salesReportLabel: "Retail Sales Report",
        itemSingular: "Grocery Item",
        itemPlural: "Grocery Items",
        productMasterLabel: "Item Master",
        productMasterAddButton: "Add Item",
        productListLabel: "Grocery Items",
        productListAddButton: "Add Item",
        importAllButtonLabel: "Import All Grocery Items",
        categorySingular: "Department / Category",
        categoryPlural: "Departments & Categories",
        categoryNavText: "Departments",
        categoryAddButton: "Add Department",
        tablesLabel: "Checkout Counters",
        tablesAddButton: "Add Counter",
        recipesLabel: "Combos & Bundles",
        recipesAddButton: "Add Combo",
        customersLabel: "Shoppers / Customers",
        customerSingular: "Customer",
        customerAddButton: "Add Customer",
        suppliersLabel: "FMCG Distributors",
        supplierSingular: "Distributor",
        supplierAddButton: "Add Distributor",
        stockOutletLabel: "Store Inventory",
        purchasesLabel: "Purchase Orders",
        purchaseAddButton: "New Purchase",
        workCategorySimpleLabel: "Grocery / Retail Billing",
        salesInvoicesTabLabel: "Sales Invoices",
        returnsTabLabel: "Returns & Credit Notes",
        returnsRegisterLabel: "Returns & Credit Notes Register",
        returnSlipTitle: "Credit Note / Return Slip",
      };

    case "WHOLESALE":
      return {
        vertical: "WHOLESALE",
        verticalLabel: "Wholesale / Distribution",
        posButtonLabel: "B2B Invoicing",
        posNavText: "B2B Wholesale Invoicing",
        posPath: "/sales",
        salesReportLabel: "Wholesale Sales Report",
        itemSingular: "Product / Goods",
        itemPlural: "Products",
        productMasterLabel: "Product Catalog",
        productMasterAddButton: "Add Product",
        productListLabel: "Wholesale Stock Items",
        productListAddButton: "Add Product",
        importAllButtonLabel: "Import All Products",
        categorySingular: "Goods Category",
        categoryPlural: "Goods Categories",
        categoryNavText: "Goods Categories",
        categoryAddButton: "Add Category",
        tablesLabel: "Sales Counters",
        tablesAddButton: "Add Counter",
        recipesLabel: "Assembly & Bundles",
        recipesAddButton: "Add Assembly",
        customersLabel: "Dealers & Retailers",
        customerSingular: "Dealer / Client",
        customerAddButton: "Add Dealer / Client",
        suppliersLabel: "Manufacturers & Vendors",
        supplierSingular: "Manufacturer / Vendor",
        supplierAddButton: "Add Manufacturer",
        stockOutletLabel: "Warehouse Stock",
        purchasesLabel: "Procurement / Inward",
        purchaseAddButton: "New Inward Entry",
        workCategorySimpleLabel: "Wholesale / Bulk Invoicing",
        salesInvoicesTabLabel: "Sales Invoices",
        returnsTabLabel: "Sales Returns & Credit Notes",
        returnsRegisterLabel: "Sales Returns & Credit Notes Register",
        returnSlipTitle: "Credit Note / Sales Return Slip",
      };

    case "ALL_IN_ONE":
    default:
      return {
        vertical: "ALL_IN_ONE",
        verticalLabel: "All-in-One POS",
        posButtonLabel: "POS",
        posNavText: "Point of Sale (POS)",
        posPath: "/sales",
        salesReportLabel: "Sales Report",
        itemSingular: "Product",
        itemPlural: "Products",
        productMasterLabel: "Product Master",
        productMasterAddButton: "Add Product",
        productListLabel: "Products",
        productListAddButton: "Add new",
        importAllButtonLabel: "Import All Products",
        categorySingular: "Category",
        categoryPlural: "Categories",
        categoryNavText: "Categories",
        categoryAddButton: "Add Category",
        tablesLabel: "Tables",
        tablesAddButton: "Add Table",
        recipesLabel: "Recipes",
        recipesAddButton: "Add Recipe",
        customersLabel: "Customers",
        customerSingular: "Customer",
        customerAddButton: "Add Customer",
        suppliersLabel: "Suppliers",
        supplierSingular: "Supplier",
        supplierAddButton: "Add Supplier",
        stockOutletLabel: "Stock by outlet",
        purchasesLabel: "Purchases",
        purchaseAddButton: "New Purchase",
        workCategorySimpleLabel: "Retail & General Billing",
        salesInvoicesTabLabel: "Sales Invoices",
        returnsTabLabel: "Returns & Credit Notes",
        returnsRegisterLabel: "Returns & Credit Notes Register",
        returnSlipTitle: "Credit Note / Return Slip",
      };
  }
}

export function useTradeTerminology(): {
  businessType: string;
  terms: TradeTerminology;
} {
  const user = useAuthStore((s) => s.user);
  const businessType =
    user?.company?.businessType ||
    localStorage.getItem("business_type") ||
    "ALL_IN_ONE";
  const terms = getTradeTerminology(businessType);
  return { businessType, terms };
}
